"use client";

import { motion } from "framer-motion";
import { Check, X, ArrowRight, Zap, User, Users as UsersIcon, Building2, Layers, Shield } from "lucide-react";

// "How to hire" section — agencyofpoly.com
//
// Canon refs (single source of truth):
//   Poly/docs/strategy/business_model_and_pricing_sot.md §6 (Two Doors, One Meter)
//   Poly/docs/strategy/digital_workforce_product_catalog.md (catalog SKUs)
//   Poly/docs/strategy/public_messaging_standards.md (required & banned words)
//
// Design intent (per user direction, May 2026):
//   - NO explicit prices on this page — pricing lives on the platform.
//     The website's job is to anchor the mental model and route the buyer
//     to the right door (try → platform, hire → call).
//   - Mental-model bridge: anchor the buyer's *new* purchase decision to
//     the three *familiar* hiring patterns they already understand —
//     freelancer, employee, agency.
//   - Add a "What Poly is / What Poly is not" clarity block to neutralise
//     misconceptions (it's not an AI tool, not an agent platform, not
//     usage-billed software).

const PLATFORM_URL = "https://poly.agencyofpoly.com/";
const BOOK_A_CALL_URL = "https://cal.com/princeps/poly-digital-workforce";

// "What Poly is / is not" — paired clarity grid
const polyIs: string[] = [
    "A digital workforce you hire by the role",
    "Named workers with memory, tools, and a monthly salary",
    "Output guaranteed against a KPI you define",
    "One bill that includes infrastructure, models, and execution receipts",
];

const polyIsNot: string[] = [
    "Not an AI tool, app, or chatbot you operate",
    "Not an agent platform you have to assemble",
    "Not metered software billed by tokens or API calls",
    "Not a freelancer marketplace or staffing agency",
];

// Mental-model bridge rows — familiar hiring pattern → Poly equivalent
type FamiliarRow = { familiar: string; nowYouCan: string };

const familiarRows: FamiliarRow[] = [
    {
        familiar: "Hire a freelancer on Upwork for one gig",
        nowYouCan: "Run one task with a digital worker — pay with credits",
    },
    {
        familiar: "Hire a part-time employee for a recurring role",
        nowYouCan: "Hire a digital worker on monthly salary",
    },
    {
        familiar: "Put a small agency on retainer for a function",
        nowYouCan: "Hire a digital team or department on monthly retainer",
    },
];

// Tier cards — qualitative anchors, no $ amounts (pricing lives on the platform)
type Tier = {
    id: string;
    icon: React.ComponentType<{ className?: string }>;
    door: "A · Try" | "B · Hire";
    name: string;
    anchorLabel: string;
    headline: string;
    includes: string[];
    cta: { label: string; href: string };
    accent?: boolean;
};

const tiers: Tier[] = [
    {
        id: "credits",
        icon: Zap,
        door: "A · Try",
        name: "Try one task",
        anchorLabel: "Pay-as-you-go credits",
        headline: "Run one task with a worker. See the output. Decide.",
        includes: [
            "Pick any worker from the catalog",
            "Assign one real task",
            "Credits debit as work runs",
            "Upgrade to a salary when usage repeats",
        ],
        cta: { label: "Try with credits", href: PLATFORM_URL },
    },
    {
        id: "worker",
        icon: User,
        door: "B · Hire",
        name: "Hire a worker",
        anchorLabel: "One named worker · monthly salary",
        headline: "A single digital worker on the payroll, full-time on one role.",
        includes: [
            "Roles like Junior SDR, Content Writer, DM Receptionist, Creative Designer, Research Analyst",
            "Included monthly work budget",
            "Output guarantee tied to a KPI you define",
            "Memory, tools, approvals, execution receipts",
        ],
        cta: { label: "Browse workers", href: PLATFORM_URL },
        accent: true,
    },
    {
        id: "team",
        icon: UsersIcon,
        door: "B · Hire",
        name: "Hire a team",
        anchorLabel: "Coordinated workforce · monthly retainer",
        headline: "Multiple workers, one outcome — they hand off to each other.",
        includes: [
            "Pre-built teams: Reception & Booking · Lead Gen · Content Production · Campaign Ops",
            "Team-level workflows and handoffs",
            "Combined monthly work budget",
            "Weekly delivery + KPI report",
        ],
        cta: { label: "Talk about a team", href: BOOK_A_CALL_URL },
    },
    {
        id: "department",
        icon: Building2,
        door: "B · Hire",
        name: "Hire a department",
        anchorLabel: "Full function · monthly retainer",
        headline: "An entire function — strategy through execution and reporting.",
        includes: [
            "Marketing · Revenue · or a custom department",
            "Org-chart-level coordination",
            "Strategy, execution, and KPI reporting cadence",
            "Managed setup included",
        ],
        cta: { label: "Talk about a department", href: BOOK_A_CALL_URL },
    },
];

export function Pricing() {
    return (
        <section id="how-to-hire" className="py-24 px-6 md:px-12 lg:px-16">
            <div className="w-full max-w-7xl mx-auto">
                {/* Section header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="text-center mb-14"
                >
                    <span className="inline-block font-body text-xs uppercase tracking-widest text-muted-foreground border border-border rounded-full px-4 py-1.5 mb-6">
                        How to hire
                    </span>
                    <h2 className="font-heading text-4xl md:text-5xl text-foreground mb-4">
                        You already know how to hire.
                    </h2>
                    <p className="font-body text-lg text-muted-foreground font-light max-w-2xl mx-auto">
                        Freelancer for a task. Employee for a role. Agency for a function.
                        Poly is the same mental model — except the workforce is digital,
                        instant, and always-on.
                    </p>
                </motion.div>

                {/* What Poly is / is not — clarity grid */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-14"
                >
                    {/* IS */}
                    <div className="rounded-2xl border border-border bg-card/40 p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary/15 text-primary">
                                <Check className="h-3.5 w-3.5" />
                            </span>
                            <span className="font-body text-xs uppercase tracking-widest text-muted-foreground">
                                What Poly is
                            </span>
                        </div>
                        <ul className="flex flex-col gap-3">
                            {polyIs.map((item, i) => (
                                <li key={i} className="flex items-start gap-2.5">
                                    <Check className="h-4 w-4 text-primary mt-1 shrink-0" />
                                    <span className="font-body text-sm md:text-base text-foreground/90 leading-snug">
                                        {item}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* IS NOT */}
                    <div className="rounded-2xl border border-border bg-card/40 p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-muted text-muted-foreground">
                                <X className="h-3.5 w-3.5" />
                            </span>
                            <span className="font-body text-xs uppercase tracking-widest text-muted-foreground">
                                What Poly is not
                            </span>
                        </div>
                        <ul className="flex flex-col gap-3">
                            {polyIsNot.map((item, i) => (
                                <li key={i} className="flex items-start gap-2.5">
                                    <X className="h-4 w-4 text-muted-foreground mt-1 shrink-0" />
                                    <span className="font-body text-sm md:text-base text-muted-foreground leading-snug">
                                        {item}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </motion.div>

                {/* Familiar → Poly bridge strip */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                    className="rounded-2xl border border-border bg-card/40 overflow-hidden mb-14"
                >
                    <div className="hidden md:grid grid-cols-[1fr_auto_1fr] gap-6 px-6 py-3 border-b border-border/60 bg-muted/30">
                        <span className="font-body text-xs uppercase tracking-widest text-muted-foreground">
                            You're familiar with
                        </span>
                        <span className="w-6" aria-hidden="true" />
                        <span className="font-body text-xs uppercase tracking-widest text-muted-foreground">
                            Now you can hire
                        </span>
                    </div>
                    <ul className="divide-y divide-border/60">
                        {familiarRows.map((row, i) => (
                            <li
                                key={i}
                                className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-2 md:gap-6 px-6 py-4 items-center"
                            >
                                <span className="font-body text-sm md:text-base text-muted-foreground">
                                    {row.familiar}
                                </span>
                                <ArrowRight className="hidden md:block h-4 w-4 text-muted-foreground/60" />
                                <span className="font-body text-sm md:text-base text-foreground">
                                    {row.nowYouCan}
                                </span>
                            </li>
                        ))}
                    </ul>
                </motion.div>

                {/* One Hire, One Bill — canon §13.1 cost decomposition + §14 pricing headline.
                    Answers the question "what am I actually paying for?" right before the
                    buyer scans the tier cards. */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                    className="mb-14"
                >
                    <div className="text-center mb-6">
                        <h3 className="font-heading text-2xl md:text-3xl text-foreground mb-2">
                            Simple salaries. Included work budget. No surprise overages.
                        </h3>
                        <p className="font-body text-sm text-muted-foreground">
                            One hire. One bill. Three things included.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="rounded-2xl border border-border bg-card/40 p-5">
                            <div className="flex items-center gap-2 mb-2">
                                <Layers className="h-4 w-4 text-primary" />
                                <span className="font-body text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Platform access
                                </span>
                            </div>
                            <p className="font-body text-sm text-foreground/90 leading-snug">
                                Infrastructure, memory, tools, approvals, and governance — always-on.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-border bg-card/40 p-5">
                            <div className="flex items-center gap-2 mb-2">
                                <Zap className="h-4 w-4 text-primary" />
                                <span className="font-body text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Work budget
                                </span>
                            </div>
                            <p className="font-body text-sm text-foreground/90 leading-snug">
                                Monthly runtime for the worker’s thinking and tools — metered against the salary.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-border bg-card/40 p-5">
                            <div className="flex items-center gap-2 mb-2">
                                <Shield className="h-4 w-4 text-primary" />
                                <span className="font-body text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Output guarantee
                                </span>
                            </div>
                            <p className="font-body text-sm text-foreground/90 leading-snug">
                                Deliverables tied to a KPI you define — with execution receipts.
                            </p>
                        </div>
                    </div>
                    <p className="font-body text-xs text-muted-foreground/70 text-center mt-4 max-w-2xl mx-auto">
                        Thinking and tools are metered underneath against the work budget — no token math on your side. If the worker needs more runtime, you top up before more work runs.
                    </p>
                </motion.div>

                {/* Tier grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-5 mb-10 pt-3">
                    {tiers.map((tier, idx) => (
                        <TierCard key={tier.id} tier={tier} index={idx} />
                    ))}
                </div>

                {/* Footer line — model selection clarity per business_model_and_pricing_sot.md §5.1 */}
                <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="font-body text-sm text-muted-foreground/80 font-light text-center max-w-3xl mx-auto"
                >
                    Every salary includes platform infrastructure, memory, tools, governance, and
                    execution receipts. Model choice is Poly's optimization problem — you buy the
                    worker and the output guarantee. Exact pricing lives inside the platform.
                </motion.p>
            </div>
        </section>
    );
}

function TierCard({ tier, index }: { tier: Tier; index: number }) {
    const Icon = tier.icon;
    const isExternal = tier.cta.href.startsWith("http");

    return (
        <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className={`relative flex flex-col rounded-2xl border p-6 transition-all duration-200 hover:-translate-y-1 ${tier.accent
                ? "border-primary/40 bg-card shadow-lg shadow-primary/5 pt-8 md:mt-3"
                : "border-border bg-card/50 hover:border-border/80"
                }`}
        >
            {tier.accent && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-foreground text-background text-[10px] uppercase tracking-widest font-semibold shadow-md shadow-foreground/20 whitespace-nowrap">
                    <Zap className="h-3 w-3" />
                    Most popular
                </span>
            )}

            {/* Door badge */}
            <div className="flex items-center gap-2 mb-4">
                <Icon className="h-4 w-4 text-muted-foreground" />
                <span className="font-body text-[10px] uppercase tracking-widest text-muted-foreground/70">
                    Door {tier.door}
                </span>
            </div>

            {/* Name + qualitative anchor (no $) */}
            <h3 className="font-heading text-xl text-foreground mb-1">{tier.name}</h3>
            <p className="font-body text-xs uppercase tracking-wider text-muted-foreground mb-4">
                {tier.anchorLabel}
            </p>

            <p className="font-body text-sm text-muted-foreground font-light mb-5 leading-relaxed">
                {tier.headline}
            </p>

            {/* Includes */}
            <ul className="flex flex-col gap-2.5 mb-6 flex-1">
                {tier.includes.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <span className="font-body text-sm text-foreground/85 leading-snug">
                            {item}
                        </span>
                    </li>
                ))}
            </ul>

            {/* CTA */}
            <a
                href={tier.cta.href}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noopener noreferrer" : undefined}
                className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 hover:-translate-y-0.5 ${tier.accent
                    ? "bg-foreground text-background hover:shadow-lg"
                    : "bg-muted text-foreground hover:bg-accent"
                    }`}
            >
                {tier.cta.label}
                <ArrowRight className="h-4 w-4" />
            </a>
        </motion.article>
    );
}
